/**
 * <park-admin-list> + <park-admin-row> — the operator's compact deep-link list,
 * lifted 1:1 from the `.yp-oplist`/`.yp-oprow` markup that
 * `components/admin/AdminLanding.astro` used to hand-roll (anton/forrest#1000).
 *
 * Where <park-admin-tile-grid> is the browsing surface ("what is edited where"),
 * this is the dense operational one: a name, a one-line description, an arrow.
 * The landing repeats it 13 times across the Operations and Customers sections.
 *
 * Single source of truth = the pure `renderParkAdminList()` function below,
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkAdminList.astro`
 *     (server-side, inside <park-admin-list data-ssr> so it is in the initial HTML), and
 *   - the Claude-Design preview, where the element parses its author-syntax
 *     <park-admin-row> children and renders client-side.
 *
 * Author syntax:
 *   <park-admin-list>
 *     <park-admin-row title="Varaukset" desc="Kaikki varaukset" href="…"></park-admin-row>
 *   </park-admin-list>
 *
 * Styling lives in wc/css/park-admin-row.css — no <style> here, no shadow DOM.
 *
 * @dsSpec design-system#FR-3 — the recurring operator deep-link row.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-admin-list";
const ROW_TAG = "park-admin-row";

export interface ParkAdminRowData {
	title?: string;
	desc?: string;
	href?: string;
}

/**
 * Pure render: (rows) → inner HTML string. No DOM access, so it is safe to call
 * server-side (the .astro wrapper) and client-side (the element). Emits the whole
 * `<div class="pa-list">…</div>`. The arrow is decorative and marked
 * aria-hidden — the link already carries its name in text (admin-shell#FR-8).
 */
export function renderParkAdminList(rows: ParkAdminRowData[]): string {
	const items = rows
		.map((row) => {
			const desc = row.desc
				? html`<span class="pa-row-desc">${esc(row.desc)}</span>`
				: "";
			return html`<a class="pa-row" href="${esc(row.href ?? "")}"><b>${esc(row.title ?? "")}</b>${desc}<span class="pa-arrow" aria-hidden="true">→</span></a>`;
		})
		.join("");
	return html`<div class="pa-list">${items}</div>`;
}

class ParkAdminList extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		const rows: ParkAdminRowData[] = [];
		const tmp = document.createElement("div");
		tmp.innerHTML = this.slotHtml;
		for (const el of tmp.querySelectorAll(ROW_TAG)) {
			rows.push({
				title: el.getAttribute("title") ?? undefined,
				desc: el.getAttribute("desc") ?? undefined,
				href: el.getAttribute("href") ?? undefined,
			});
		}
		return renderParkAdminList(rows);
	}
}

// Author-syntax only: parsed by <park-admin-list>. See park-admin-tile.ts for
// why this extends ParkElement rather than HTMLElement.
class ParkAdminRow extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}
	protected render(): string {
		return this.slotHtml;
	}
}

defineElement(TAG, ParkAdminList);
defineElement(ROW_TAG, ParkAdminRow);

export { ParkAdminList, ParkAdminRow };
